if (!customElements.get('installation-slots-modal')) {
  class InstallationSlotsModal extends HTMLElement {
    static CACHE_MS = 5 * 60 * 1000;

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.slotsUrl = this.dataset.slotsUrl;
      this.dialog = this.querySelector('dialog');
      this.body = this.querySelector('[data-body]');
      this.selectionText = this.querySelector('[data-selection]');
      this.bookButton = this.querySelector('[data-book]');
      this.bookLabel = this.querySelector('[data-book-label]');
      this.selected = null;
      this.loadedAt = 0;
      this.waNumber = this.dataset.waNumber;

      const opener = document.getElementById(`InstallationButton-${this.sectionId}`);
      if (opener) opener.addEventListener('click', () => this.open());

      this.querySelector('[data-close]').addEventListener('click', () => this.close());
      // Clicks on the ::backdrop land on the <dialog> itself.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('close', () => document.body.classList.remove('overflow-hidden'));
      this.bookButton.addEventListener('click', () => this.book());
    }

    open() {
      if (!this.dialog.open) this.dialog.showModal();
      document.body.classList.add('overflow-hidden');
      if (Date.now() - this.loadedAt > InstallationSlotsModal.CACHE_MS) this.load();
      this.track('installation_slots_open');
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    async load() {
      if (!this.slotsUrl) {
        this.renderMessage('Online timeslots aren’t available right now. Message us on WhatsApp and we’ll schedule your installation.');
        return;
      }

      this.renderMessage('Loading timeslots…');
      try {
        const response = await fetch(this.slotsUrl, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (data.success === false || !Array.isArray(data.slots)) throw new Error(data.message || 'Unexpected response');
        this.loadedAt = Date.now();
        this.render(data.slots);
      } catch (error) {
        console.error('Installation slots failed to load', error);
        this.renderMessage('We couldn’t load the timeslots. Message us on WhatsApp and we’ll schedule your installation.');
      }
    }

    renderMessage(text) {
      this.body.innerHTML = '';
      const p = document.createElement('p');
      p.className = 'bb-install-modal__state';
      p.textContent = text;
      this.body.appendChild(p);
      this.setSelection(null);
    }

    render(slots) {
      const days = new Map();
      slots
        .filter((slot) => !slot.past)
        .forEach((slot) => {
          if (!days.has(slot.date)) days.set(slot.date, []);
          days.get(slot.date).push(slot);
        });

      const bookable = slots.filter((slot) => slot.bookableOnline && !slot.past).length;
      if (!days.size || !bookable) {
        this.renderMessage('All online timeslots for the next two weeks are taken. Message us on WhatsApp and we’ll find a time for you.');
        return;
      }

      // Keep each start time in its own column so times line up down the list, even on
      // days where earlier slots have already passed.
      const startTimes = [...new Set(slots.map((slot) => slot.startTime))].sort();

      const list = document.createElement('ol');
      list.className = 'bb-install-modal__days';
      list.setAttribute('role', 'list');
      list.style.setProperty('--slot-columns', startTimes.length);

      days.forEach((daySlots, date) => {
        const item = document.createElement('li');
        item.className = 'bb-install-modal__day';

        const heading = document.createElement('div');
        heading.className = 'bb-install-modal__date';
        const { weekday, dayMonth } = this.formatDate(date);
        heading.innerHTML = `<span class="bb-install-modal__weekday"></span><span class="bb-install-modal__daymonth"></span>`;
        heading.firstChild.textContent = weekday;
        heading.lastChild.textContent = dayMonth;
        item.appendChild(heading);

        const slotList = document.createElement('div');
        slotList.className = 'bb-install-modal__slots';
        slotList.setAttribute('role', 'group');
        slotList.setAttribute('aria-label', `${weekday} ${dayMonth}`);

        daySlots
          .sort((a, b) => a.startTime.localeCompare(b.startTime))
          .forEach((slot) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'bb-install-modal__slot';
            button.style.gridColumn = String(startTimes.indexOf(slot.startTime) + 1);
            const timeLabel = `${this.formatTime(slot.startTime)} – ${this.formatTime(slot.endTime)}`;
            button.innerHTML = '<span class="bb-install-modal__time"></span><span class="bb-install-modal__status"></span>';
            button.firstChild.textContent = timeLabel;

            if (slot.bookableOnline) {
              button.classList.add('bb-install-modal__slot--open');
              button.lastChild.textContent = 'Available';
              button.setAttribute('aria-pressed', 'false');
              button.addEventListener('click', () => this.select(button, { date, weekday, dayMonth, timeLabel }));
            } else {
              button.classList.add('bb-install-modal__slot--full');
              button.lastChild.textContent = 'Unavailable';
              button.disabled = true;
            }
            slotList.appendChild(button);
          });

        item.appendChild(slotList);
        list.appendChild(item);
      });

      this.body.innerHTML = '';
      this.body.appendChild(list);
      this.setSelection(null);
    }

    select(button, slot) {
      this.body.querySelectorAll('.bb-install-modal__slot[aria-pressed="true"]').forEach((other) => {
        other.setAttribute('aria-pressed', 'false');
      });
      button.setAttribute('aria-pressed', 'true');
      this.setSelection(slot);
      this.track('installation_slot_select', { slot_date: slot.date, slot_time: slot.timeLabel });
    }

    setSelection(slot) {
      this.selected = slot;
      if (slot) {
        this.selectionText.innerHTML = 'Selected: <strong></strong>';
        this.selectionText.lastChild.textContent = `${slot.weekday} ${slot.dayMonth}, ${slot.timeLabel}`;
        this.bookLabel.textContent = 'Install Now';
      } else {
        this.selectionText.textContent = 'Select a timeslot, or ask us about installation.';
        this.bookLabel.textContent = 'Ask about installation';
      }
    }

    book() {
      const title = this.dataset.productTitle;
      const sku = this.dataset.productSku ? ` (SKU ${this.dataset.productSku})` : '';
      const when = this.selected ? ` on ${this.selected.weekday} ${this.selected.dayMonth}, ${this.selected.timeLabel}` : '';
      const message = `Hi, I'd like to book installation for ${title}${sku}${when}. ${window.location.href}`;
      this.track('installation_book_click', { has_slot: Boolean(this.selected) });
      // Same event the product page's other WhatsApp buttons send, so CTWA reporting includes these.
      this.track('ctwa_click', { touchpoint: 'installation_modal', branch: 'Installation' });

      // Installation requests go straight to the installation team's WhatsApp, not the branch picker.
      const params = new URLSearchParams({ text: message });
      if (this.waNumber) params.set('phone', this.waNumber);
      window.open(`https://api.whatsapp.com/send?${params}`, '_blank', 'noopener,noreferrer');
    }

    // Slot dates are Malaysia calendar dates (YYYY-MM-DD); format them without shifting time zones.
    formatDate(isoDate) {
      const [year, month, day] = isoDate.split('-').map(Number);
      const date = new Date(Date.UTC(year, month - 1, day));
      const options = { timeZone: 'UTC' };
      return {
        weekday: date.toLocaleDateString('en-MY', { ...options, weekday: 'short' }),
        dayMonth: date.toLocaleDateString('en-MY', { ...options, day: 'numeric', month: 'short' }),
      };
    }

    formatTime(hhmm) {
      const [hours, minutes] = hhmm.split(':').map(Number);
      const suffix = hours >= 12 ? 'pm' : 'am';
      const hour12 = hours % 12 || 12;
      return minutes ? `${hour12}:${String(minutes).padStart(2, '0')} ${suffix}` : `${hour12} ${suffix}`;
    }

    track(event, extra = {}) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, product_sku: this.dataset.productSku, ...extra });
    }
  }

  customElements.define('installation-slots-modal', InstallationSlotsModal);
}
